// retrieve the list of dismissed advisory ID's from browser storage
import { LocalStorageItem, LocalStorageItemKey, StorageService } from 'src/components/utils/storage-service';
import { EXP_1_WEEK_MINS, EXP_24_HOURS_MINS } from 'src/commons/constants';
import { Advisory } from 'aws-sdk/clients/awsdlomni';
import { listAdvisories } from 'src/api/notifications';
import { max } from 'lodash';

export const LIST_ADVISORIES_FILTER_GLOBAL = 'GLOBAL'; // used for ownerId = GLOBAL when listing global advisories

export const getDismissedAdvisories = async () => {
  const storageInstance = StorageService.getInstance();
  const currentValue = await storageInstance.getItem(LocalStorageItemKey.DismissedAdvisories);
  if (!currentValue) {
    await storageInstance.setItem(LocalStorageItemKey.DismissedAdvisories, new LocalStorageItem('[]', EXP_1_WEEK_MINS));
    return [];
  }
  const dismissedAdvisoriesString = await storageInstance.getItem(LocalStorageItemKey.DismissedAdvisories);
  let dismissedAdvisories: string[] = JSON.parse(dismissedAdvisoriesString.data as string);
  return dismissedAdvisories;
};

export const getDismissedAdvisoriesExpiryMinutes = async () => {
  const storageInstance = StorageService.getInstance();
  const dismissedAdvisoriesLocalStorageItem = await storageInstance.getItem(LocalStorageItemKey.DismissedAdvisories);
  if (!dismissedAdvisoriesLocalStorageItem) {
    return EXP_1_WEEK_MINS; // default value
  }
  return dismissedAdvisoriesLocalStorageItem.expiryMinutes;
};

// add an advisory ID to browser storage and dispatch an event (App.tsx listens to this event)
export const addDismissedAdvisory = async (advisory: Advisory) => {
  const storageInstance = StorageService.getInstance();
  let dismissedAdvisories = await getDismissedAdvisories();
  if (!dismissedAdvisories.includes(advisory.advisoryId)) {
    dismissedAdvisories.push(advisory.advisoryId);
  }
  // take the max of the current expiration and the expiration for this advisory
  let resolveAfterDays = 30; // default, in case it is not defined in the advisory
  if (advisory.lifecycleConfiguration?.autoResolveAfterDays != undefined) {
    resolveAfterDays = advisory.lifecycleConfiguration.autoResolveAfterDays;
  }
  const expireAfterMins = max([await getDismissedAdvisoriesExpiryMinutes(), EXP_24_HOURS_MINS * resolveAfterDays]);
  await storageInstance.setItem(
    LocalStorageItemKey.DismissedAdvisories,
    new LocalStorageItem(JSON.stringify(dismissedAdvisories), expireAfterMins),
  );
  window.dispatchEvent(new StorageEvent('storage'));
};

// fetch all global advisories (feature announcements)
export const fetchGlobalAdvisories = async function () {
  let advisories: Advisory[] = [];
  try {
    let nextToken = undefined;
    while (true) {
      const listAdvisoriesResult = await listAdvisories({
        ownerId: 'GLOBAL',
        filter: {
          quickFilters: {
            allAdvisories: true,
            advisoriesForDataYouHaveAccessTo: false,
            advisoriesForDataYouOwn: false,
          },
          types: {
            globalAnnouncement: true,
            defaultAdvisory: false,
          },
          statuses: {
            active: true,
            canceled: false,
            resolved: false,
            draft: false,
          },
        },
        nextToken: nextToken,
      });
      advisories.push(...listAdvisoriesResult.advisories);
      nextToken = listAdvisoriesResult.nextToken;
      if (!nextToken) break;
    }
    console.info('Fetched global advisories with %s entries', advisories.length);
    return advisories;
  } catch (e) {
    console.log('Error when fetching global advisories.', e);
    return [];
  }
};

// fetch all active advisories for the current user, and update the side nav and flashbar accordingly.
export const fetchActiveAdvisoriesForDataYouConsume = async function (
  allowlisted: boolean,
  groupId: string,
  workspaceId: string,
) {
  // if user is not allowlisted, do not look for active advisories as there won't be any
  if (!allowlisted) return [];

  const groupOrWorkspaceId =
    workspaceId == undefined || workspaceId == null || workspaceId == 'null' ? groupId : workspaceId;
  let advisories: Advisory[] = [];
  try {
    let nextToken = undefined;
    while (true) {
      const listAdvisoriesResult = await listAdvisories({
        ownerId: groupOrWorkspaceId,
        filter: {
          quickFilters: {
            allAdvisories: false,
            advisoriesForDataYouHaveAccessTo: true,
            advisoriesForDataYouOwn: false,
          },
          statuses: {
            active: true,
            canceled: false,
            resolved: false,
            draft: false,
          },
        },
        nextToken: nextToken,
      });
      advisories.push(...listAdvisoriesResult.advisories);
      nextToken = listAdvisoriesResult.nextToken;
      if (!nextToken) break;
    }
    console.info('Fetched active advisories with %s entries', advisories.length);
    return advisories;
  } catch (e) {
    console.log('Error when fetching active advisories.', e);
    return [];
  }
};
